import React, { useMemo, useState } from 'react';
import Modal from '../Modal/Modal';
import { Controller } from '../../store/models/controllers';

type AddControllerModalProps = {
  isVisible: boolean;
  availableControllers: Controller[];
  onClose: () => void;
  onAdd: (serialNumbers: string[]) => void;
};

// Numeric-aware ("smart") sort so THT-9 sorts before THT-13, which plain
// string comparison would get wrong.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const AddControllerModal: React.FC<AddControllerModalProps> = ({
  isVisible,
  availableControllers,
  onClose,
  onAdd,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const sortedControllers = useMemo(
    () => [...availableControllers].sort((a, b) => collator.compare(a.name, b.name)),
    [availableControllers],
  );

  const filteredControllers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return sortedControllers;
    }

    return sortedControllers.filter((controller) => controller.name.toLowerCase().includes(query));
  }, [sortedControllers, search]);

  const toggleSelected = (serial_number: string) => {
    setSelected((prev) => (
      prev.includes(serial_number)
        ? prev.filter((item) => item !== serial_number)
        : [...prev, serial_number]
    ));
  };

  const handleClose = () => {
    setSearch('');
    setSelected([]);
    onClose();
  };

  const handleAdd = () => {
    onAdd(selected);
    setSearch('');
    setSelected([]);
  };

  return (
    <Modal
      isVisible={isVisible}
      modalTitle="Add controller"
      onClose={handleClose}
      onAccept={handleAdd}
      acceptBtnText="Add"
      cancelBtnText="Cancel"
      acceptBtnDisabled={selected.length === 0}
    >
      <div className="c-add-controller-modal flex flex-col gap-y-4 text-left">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search controllers"
          className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-2 px-4 text-sm outline-none transition focus:border-primary dark:border-form-strokedark dark:bg-form-input"
        />
        <div className="flex max-h-80 flex-col gap-y-1 overflow-auto">
          {filteredControllers.length === 0 && (
            <p className="py-4 text-center text-sm text-body dark:text-bodydark">No controllers available</p>
          )}
          {filteredControllers.map((controller) => {
            const isSelected = selected.includes(controller.serial_number);

            return (
              <label
                key={controller.serial_number}
                className="flex cursor-pointer items-center gap-x-3 rounded-md px-2 py-2 hover:bg-gray-2 dark:hover:bg-meta-4"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelected(controller.serial_number)}
                  className="h-4 w-4"
                />
                <span className="text-sm text-black dark:text-white">{controller.name}</span>
              </label>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default AddControllerModal;
